import { useEffect, useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { SideNav } from './sidenav';
import { TopNav } from './topnav';
import { Outlet } from 'react-router-dom';
import { useAuthContext } from '../../contexts/auth-context';
import { useQuery } from '@apollo/client';
import { GET_USER_CURRENT_SHOP_DETAILS } from '../../gql/queries';
import ImpersonationBanner from '../impersonation/impersonation-banner';
import {
	readCollapsedPreference,
	useNavTransition,
	useSideNavWidth,
	writeCollapsedPreference,
} from './layout-constants';

const LayoutContainer = styled('div')(({ theme }) => ({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'column',
	width: '100%',
	padding: theme.spacing(2),
	[theme.breakpoints.down('md')]: {
		padding: theme.spacing(1)
	}
}));

export const Layout = () => {
	const theme = useTheme();
	const mdUp = useMediaQuery(theme.breakpoints.up('md'));
	const { jwtDecoded, shopId, locationId, setShopDetails } = useAuthContext();
	const { data } = useQuery(GET_USER_CURRENT_SHOP_DETAILS, { variables: { shopId, locationId, userId: jwtDecoded?.user_id } });

	/* Desktop rail preference survives reloads; the mobile drawer always starts closed. */
	const [collapsed, setCollapsed] = useState<boolean>(readCollapsedPreference);
	const [mobileOpen, setMobileOpen] = useState(false);
	const navWidth = useSideNavWidth(collapsed);
	const transition = useNavTransition(['padding-left']);

	const isImpersonating = jwtDecoded?.isImpersonating || false;

	useEffect(() => {
		if (data && data.getUserCurrentShopDetails) {
			setShopDetails(data.getUserCurrentShopDetails);
		}
	}, [data]);

	useEffect(() => {
		writeCollapsedPreference(collapsed);
	}, [collapsed]);

	/* Crossing up into desktop must not leave the mobile drawer stuck open. */
	useEffect(() => {
		if (mdUp) setMobileOpen(false);
	}, [mdUp]);

	const handleMenuToggle = () => {
		if (mdUp) setCollapsed((previous) => !previous);
		else setMobileOpen((previous) => !previous);
	};

	return (
		<>
			<TopNav onMenuToggle={handleMenuToggle} collapsed={collapsed} />
			<SideNav
				open={mobileOpen}
				onClose={() => setMobileOpen(false)}
				collapsed={collapsed}
				onToggleCollapse={() => setCollapsed((previous) => !previous)}
			/>
			<Box sx={{ display: 'flex', flex: '1 1 auto', maxWidth: '100%', pl: `${navWidth}px`, transition }}>
				<LayoutContainer>
					{isImpersonating && jwtDecoded?.impersonator && (
						<ImpersonationBanner
							impersonatedUserEmail={jwtDecoded.email}
							impersonatedUserPhone={jwtDecoded.phone}
						/>
					)}
					<Outlet />
				</LayoutContainer>
			</Box>
		</>
	);
};
